import clone from "just-clone";
import { dateFormat } from "@/utils";

let nextSlotId = 1;
let shifts = {
    early: {
        hours: [7, 8, 9, 10],
        slots: []
    },
    afternoon: {
        hours: [11, 12, 13, 14, 15],
        slots: []
    },
    late: {
        hours: [16, 17, 18, 19, 20],
        slots: []
    }
};

const dateToTimestampSeconds = date => Math.round(date.getTime() / 1000);

const dateIsOnQuarterHour = date => date.getMinutes() % 15 == 0;

const slotFollowsConsecutiveSlots = (slot, consecutiveSlots) => {
    const lastConsecutiveSlot = consecutiveSlots[consecutiveSlots.length - 1];
    return (dateToTimestampSeconds(slot.starts_at) - dateToTimestampSeconds(lastConsecutiveSlot.starts_at)) <= 300;
};

const slotShift = slotStartsAt => {
    const hour = slotStartsAt.getHours();
    if (hour >= shifts.late.hours.reduce((prev, curr) => { return curr < prev ? curr : prev; }, 999)) return "late";
    if (hour <= shifts.early.hours.reduce((prev, curr) => { return curr > prev ? curr : prev; }, -1)) return "early";
    return "afternoon";
};

const fullLengthSlot = (slot, dc) => {
    const fls = {
        id: nextSlotId,
        dc_id: slot.dc_id,
        dc_proportion_difference: dc.proportion_difference || 0,
        starts_at: slot.starts_at,
        ends_at: new Date(slot.starts_at.getTime() + (60000 * dc.consultation_length)),
        shift: slotShift(slot.starts_at),
        is_selected: false
    };

    nextSlotId += 1;
    return fls;
};

class AvailableSlotsCompiler {
    //  Compile ONE ITERATION of available slots.
    //  IE... params.slots is 4 weeks of availability
    //  But params.appointments is only ever 1 week of appointments
    //  So compile() returns ... 1 week's worth of available slots
    getDCSlots() { return this.slotsByDC; }
    getDeduplicatedSlots() { return this.deduplicatedSlots; }
    getSelectedSlots() { return this.selection; }
    getSlotsByDay() { return this.slotsByDay; }

    compile(params) {
        // console.log(`available-slots-compiler.compile.params`, params);

        this.showDevLog = false;
        nextSlotId = 1;

        this.config = {
            logMode: /*params.logMode ?? */false,
            dc: params.preferredDC,
            dcs: params.dcs,
            clinicConsultRoomCount: params.clinicConsultRoomCount,
            appointmentsStartAt: dateToTimestampSeconds(params.appointmentsStartAt),
            appointmentsEndAt: dateToTimestampSeconds(params.appointmentsEndAt)
        };

        //  params.slots is a long array of minimal 5-min slot objects with the structure:
        //
        //  {
        //      dc_id: 123,
        //      starts_at: "2022-10-03 15:00:00"
        //  }
        //  
        //  Willow API always returns 4 weeks of slots, but only ever 1 week of appointments
        //
        //  This class's task is to compile 1 week's worth of slots - full length slots matching the same
        //  time period covered by the appointments.
        //  So... we can filter the slots down to just those within the time range of the given appointments,
        //  which might lessen the load of turning 5-min appointments into full-length appointments.

        this.preferredDCId = (this.config.dc && this.config.dc.id) ? this.config.dc.id : false;

        this.slots = params.slots.filter(slot => {
            try {
                const slotStartsAt = dateToTimestampSeconds(slot.starts_at);
                return (slotStartsAt >= this.config.appointmentsStartAt) && (slotStartsAt < this.config.appointmentsEndAt);
            }
            catch (err) {
                console.error(err);
                console.log(`Error filtering slots by date range`, slot);
            }
        });

        this.appointments = params.appointments;
        this.slotsByDC = this.config.dcs.map(dc => { return { dc, fiveMinSlots: [], fullLenSlots: [] }; });
        this.slotsByDay = [];

        this.hardFilterFiveMinSlotsByPreferredDCId();
        this.compileDCsFiveMinSlots();
        this.filterDCsFiveMinSlots();
        this.compileDCsFullLenSlots();

        this.compileDeduplicatedFullLenSlots();

        this.compileSelectionOfSlots();
        this.compileSlotsByDay();
    }

    hardFilterFiveMinSlotsByPreferredDCId() {
        if(this.preferredDCId) {
            this.slots = this.slots.filter(slot => slot.dc_id == this.preferredDCId);
        }
    }

    compileDCsFiveMinSlots() {
        this.slotsByDC.forEach(dcSlots => {
            dcSlots.fiveMinSlots = this.slots.filter(slot => slot.dc_id == dcSlots.dc.id);

        });
        this.slotsByDC = this.slotsByDC.filter(dcSlots => dcSlots.fiveMinSlots.length ? true : false);

        if (this.showDevLog) {
            console.log(`compileDCsFiveMinSlots finished compiling this.slotsByDC`);
        }
    }

    filterDCsFiveMinSlots() {
        this.slotsByDC.forEach(dcSlots => {
            const dcAppointments = this.appointments.filter(appt => appt.dc_id == dcSlots.dc.id);
            const otherDCsAppointments = this.appointments.filter(appt => appt.dc_id != dcSlots.dc.id);

            dcSlots.fiveMinSlots = dcSlots.fiveMinSlots.filter(fiveMinSlot => {
                const slotStartsAt = dateToTimestampSeconds(fiveMinSlot.starts_at);
                const slotEndsAt = slotStartsAt + 300;

                if (slotEndsAt < this.config.appointmentsStartAt || slotStartsAt > this.config.appointmentsEndAt) return false;

                if (dcAppointments.find(appt => {
                    return (dateToTimestampSeconds(appt.starts_at) < slotEndsAt) && (dateToTimestampSeconds(appt.ends_at) > slotStartsAt);
                })) return false;

                //  Filter out any slot where there are enough other DCs' appointments to use all the clinic's consult rooms
                const clashingOtherDCIds = otherDCsAppointments
                    .filter(appt => {
                        return appt.requires_consult_room
                            && (dateToTimestampSeconds(appt.starts_at) < slotEndsAt)
                            && (dateToTimestampSeconds(appt.ends_at) > slotStartsAt);
                    })
                    .reduce((dcIds, appt) => {
                        if (dcIds.indexOf(appt.dc_id) < 0) dcIds.push(appt.dc_id);
                        return dcIds;
                    }, []);

                // if ((dcSlots.dc.id == 17) && (fiveMinSlot.starts_at.getDate() == 8) && clashingOtherDCIds.length) {
                //     console.log(`TS fiveMinSlot has clashingOtherDCIds`, fiveMinSlot, clashingOtherDCIds);
                // }

                return clashingOtherDCIds.length < this.config.clinicConsultRoomCount;
            });
        });

        if (this.showDevLog) {
            console.log(`filterDCsFiveMinSlots finished filtering this.slotsByDC`, this.slotsByDC);
        }

        // console.log(`Filtered DCs 5-min slots`, this.slotsByDC.find(dcSlots => dcSlots.dc.id == 17).fiveMinSlots.map(
        //     slot => { return slot.starts_at; }
        // ));
    }

    compileDCsFullLenSlots() {
        this.slotsByDC.forEach(dcSlots => {
            const dc = dcSlots.dc;
            const length = dc.consultation_length;
            const fiveMinSlotsPerFullLenSlot = Math.floor(length / 5);
            const fullLenSlots = [], consecutiveSlots = [];

            dcSlots.fiveMinSlots.sort((a, b) => a.starts_at.getTime() <= b.starts_at.getTime() ? -1 : 1);

            dcSlots.fiveMinSlots.forEach(fiveMinSlot => {
                // this.showDevLog = (fiveMinSlot.starts_at.getDate() == 8) && (dc.id == 17);

                if (consecutiveSlots.length >= fiveMinSlotsPerFullLenSlot) {
                    fullLenSlots.push(fullLengthSlot(consecutiveSlots[0], dc));
                    // if (this.showDevLog) {
                    //     console.log(`Just pushed fullLenSlot based on`, clone(consecutiveSlots));
                    // }

                    consecutiveSlots.splice(0, consecutiveSlots.length);
                }
                else {
                    // if (this.showDevLog) {
                    //     console.log(`consecutiveSlots contains ${consecutiveSlots.length || 0} fiveMinSlots...`);
                    // }

                    if (consecutiveSlots.length) {
                        if (slotFollowsConsecutiveSlots(fiveMinSlot, consecutiveSlots)) {
                            // if (this.showDevLog) {
                            //     console.log(`fiveMinSlot slot follows consecutiveSlots`, clone(fiveMinSlot));
                            // }
                            consecutiveSlots.push(fiveMinSlot);
                            // if (this.showDevLog) {
                            //     console.log(`Pushed fiveMinSlot onto consecutiveSlots`, clone(consecutiveSlots));
                            // }
                        }
                        else {
                            consecutiveSlots.splice(0, consecutiveSlots.length);
                            // if (this.showDevLog) {
                            //     console.log(`fiveMinSlot slot DOESN'T follow consecutiveSlots`, clone(fiveMinSlot));
                            // }
                            if (dateIsOnQuarterHour(fiveMinSlot.starts_at)) {
                                consecutiveSlots.push(fiveMinSlot);
                                // if (this.showDevLog) {
                                //     console.log(`fiveMinSlot is on quarter hour - pushed onto empty  consecutiveSlots...`, clone(fiveMinSlot));
                                // }
                            }
                        }
                    }
                    else {
                        // if (this.showDevLog) {
                        //     console.log(`consecutiveSlots is empty...`);
                        // }
                        if (dateIsOnQuarterHour(fiveMinSlot.starts_at)) {
                            consecutiveSlots.push(fiveMinSlot);
                            // if (this.showDevLog) {
                            //     console.log(`fiveMinSlot is on quarter hour - pushed onto empty  consecutiveSlots...`, clone(fiveMinSlot));
                            // }
                        }
                        else {
                            // if (this.showDevLog) {
                            //     console.log(`fiveMinSlot is NOT on quarter hour - doing nothing...`, clone(fiveMinSlot));
                            // }
                        }
                    }
                }
            });

            //  When forEach ends, we might have just compiled enough consecutive 5-min slots to make a full length slot
            if (consecutiveSlots.length >= fiveMinSlotsPerFullLenSlot) {
                fullLenSlots.push(fullLengthSlot(consecutiveSlots[0], dc));
            }

            dcSlots.fullLenSlots = fullLenSlots;
            dcSlots.fullLenSlots.sort((a, b) => dateToTimestampSeconds(a.starts_at) <= dateToTimestampSeconds(b.starts_at) ? -1 : 1);

            // if (dcSlots.dc.id == 17) {
            //     console.log(`fullLenSlots`, dcSlots.fullLenSlots.map(slot => slot.starts_at).filter(d => {
            //         return d.getDate() == 7;
            //     }));
            // }
        });

        if (this.showDevLog) {
            console.log(`compileDCsFullLenSlots finished working on this.slotsByDC`, this.slotsByDC);
        }
    }

    compileDeduplicatedFullLenSlots() {
        //  deduplicatedSlots is an array of slots.
        //  For some startTimes, there was likely only one slot starting at that time, and it might be
        //      for the preferred/referred-to DC and it might not.
        //  For other startTimes, there may have been > 1 slot starting at that time; in this case,
        //      slots will have been deduplicated favouring the preferred/referred-to DC,
        //      and failing that, favouring DCs who are most behind their bookings target.

        //  Handle case where there's only 1 or 0 DC/s?
        if (this.slotsByDC.length <= 1) {
            this.deduplicatedSlots = this.slotsByDC.length ? this.slotsByDC[0].fullLenSlots : [];
        }

        this.deduplicatedSlots = [];
        let time = this.config.appointmentsStartAt, slot;

        while (time < this.config.appointmentsEndAt) {
            slot = this.deduplicatedFullLenSlotStartingAt(time);
            if (slot) {
                this.deduplicatedSlots.push(slot);

                // if (slot.starts_at.getDate() == 7) {
                //     console.log(`deduplicatedFullLenSlotStartingAt ${slot.starts_at.toLocaleString("en-GB")}, DC# ${slot.dc_id}`, slot);
                // }
            }
            time += 900;//  Add 15 mins
        }

        if(this.showDevLog) {
            console.log(`compileDeduplicatedFullLenSlots finished`, this.deduplicatedSlots);
        }
    }

    deduplicatedFullLenSlotStartingAt(time) {
        const slots = this.slotsByDC.reduce((acc, cur) => {
            const slot = cur.fullLenSlots.find(s => dateToTimestampSeconds(s.starts_at) == time);
            if (slot) acc.push(slot);
            return acc;
        }, []);

        if (!slots.length) return false;

        if (slots.length <= 1) return slots[0];

        //  Strategy: slots all have dc_proportion_difference values
        //      These might all be the same, but might all be different.
        //      If they're the same we need to achieve a random selection.
        //      If they're different we need to choose the slot with the LOWEST value of dc_proportion_difference.
        //
        //      So: sort slots into a random order
        //      Then, work through slots: choose the 1st slot, then for each subsequent slot choose THAT slot if 
        //      its value of dc_proportion_difference is lower than that of the currently selected slot.
        //
        //      If all dc_proportion_difference values are the same... that'll always be the 1st slot in the (randomly sorted) array
        //
        //      Else, it should always assign the slot with the DC with the "worst" proportion of bookings vs their target

        slots.sort(() => (Math.random() * 2) - 1);

        //  slots is an array of slots starting at a certain time. Might be > 1 element in array if > 1 DC has availability starting
        //  at that time.
        //  So... slots is sorted randomly, then use Array.reduce with an initial "accumulator" value of 1st slot in array;
        //  Favour slots belonging to preferred DC (if set) otherwise prefer slot for DC doing worst against target
        return slots.reduce((prev, cur) => {
            if (!this.preferredDCId) {//  User wasn't referred to a specific DC...
                //  Return slot whose DC is doing worse vs bookings target
                return cur.dc_proportion_difference < prev.dc_proportion_difference ? cur : prev;
            }

            //  User was referred to a specific DC
            //  In the 1st iteration of this code block, prev is slots[0] so... if prev.dc_id matches this.preferredDCId, return prev.
            if (prev.dc_id == this.preferredDCId) return prev;

            //  If current slot matches this.preferredDCId, return it, otherwise return prev.
            return cur.dc_id == this.preferredDCId ? cur : prev;
        }, slots[0]);
    }

    compileSelectionOfSlots() {
        //  Process:
        //
        //      1   Compile an array of "days and shifts"
        //      2   Per day... per shift...
        //      3       Try to get 1 slot per hour
        //      4       Make up any shortfall by getting n other slots in the same shift
        // early: { starts: 6, ends: 12 },
        // afternoon: { starts: 12, ends: 17 },
        // late: { starts: 17, ends: 23 },

        let days = [];

        days = this.deduplicatedSlots.reduce((acc, slot) => {
            let day = clone(slot.starts_at);
            day.setHours(0, 0, 0, 0);
            const t = day.getTime();
            if (!acc.find(d => d.date.getTime() == t)) acc.push({
                date: day,
                shifts: clone(shifts)
            });

            return acc;
        }, []);

        this.selection = [];

        days.forEach(day => {
            (["early", "afternoon", "late"]).forEach(shiftName => {
                this.selectionOfSlotsByDayAndShift(day, shiftName).forEach(slot => { this.selection.push(slot); });
            });
        });

        this.selection.sort((slotA, slotB) => slotA.starts_at.getTime() > slotB.starts_at.getTime() ? 1 : -1);

        if(this.showDevLog) {
            console.log(`selectionOfSlots finished`, this.selection);
        }
    }

    selectionOfSlotsByDayAndShift(day, shiftName) {
        // this.showDevLog = day.date.getDate() == 7;

        const selection = [], hours = day.shifts[shiftName].hours;

        hours.forEach(hour => {
            const slot = this.preferredSlotByDayAndHour(day, hour);
            if (slot) selection.push(slot);
        });

        if (!selection.length || (selection.length < hours.length)) {
            this.topUpSlotsByDayAndShift(day, shiftName, selection).forEach(slot => { selection.push(slot); });
        }

        // if(this.showDevLog) {
        //     console.log(`selectionOfSlotsByDayAndShift, 7 Dec`, selection);
        // }

        return this.bestFourSlotsInDayAndShiftFrom(selection);
    }

    bestFourSlotsInDayAndShiftFrom(selection) {
        //  Here, selection is a simple array of full-length slot objects, within a shift on a specific day.
        //  The target is "four slots per shift" but there are >= 5 hours (2022-12-01: exactly 5 hours) in each shift
        //  So there's a possibility that:
        //      There's a preferredDCId (a referred DC)
        //      One or more of their slots are mixed in with other slots
        //      The preferred DC's slot might not be displayed, because the app only shows 4 per shift
        //  This means that ***if there's a preferred DC*** we need to filter selection by removing slots for other DCs...
        //  Until we get to a point where there are only slots for the preferred DC, or still a mix but max 4 slots
        if (selection.length <= 4) return selection;

        //  Sort randomly
        selection.sort(() => (Math.random() * 2) - 1);

        //  We know there's 5 or more slots
        //  If there IS a preferred DC, and selection contains one or more slots for that DC...
        //  Try to weed out other DCs' slots until selection.length == 4
        if (this.preferredDCId && selection.find(slot => slot.dc_id == this.preferredDCId)) {
            while (selection.find(slot => slot.dc_id != this.preferredDCId) && (selection.length > 4)) {
                const nonPreferredDCSlot = selection.find(slot => slot.dc_id != this.preferredDCId);
                if (nonPreferredDCSlot) {
                    const idx = selection.map(slot => slot.id).indexOf(nonPreferredDCSlot.id);
                    selection.splice(idx, 1);
                }
            }
        }

        if (selection.length > 4) selection.splice(4, selection.length - 4);

        //  Selection will be sorted in time order when passed back to this.selectionOfSlots
        return selection;
    }

    preferredSlotByDayAndHour(day, hour) {
        const dayTimestamp = day.date.getTime();

        //  Get slots in hour
        const slotsInHour = this.deduplicatedSlots.filter(slot => {
            let slotDay = clone(slot.starts_at);
            slotDay.setHours(0, 0, 0, 0);
            return (slotDay.getTime() === dayTimestamp) && (slot.starts_at.getHours() === hour);
        });

        //  Handle cases where there's 0 or 1 slotsInHour
        if (!slotsInHour.length) return false;
        if (slotsInHour.length <= 1) return slotsInHour[0];

        //  If there's a preferred DC
        //      Filter slots down again, to slots in the relevant 1-hour period for that DC.
        //      If there are any, return... a randomly-selected slot
        if (this.preferredDCId) {
            const preferredDCSlotsInHour = slotsInHour.filter(slot => slot.dc_id === this.preferredDCId);
            if (preferredDCSlotsInHour.length) return this.randomSlotFrom(preferredDCSlotsInHour);
        }

        //  If there's no preferred DC - or if there are no slots by that DC in that 1-hour period -
        //  First, sort slotsInHour randomly, then in ascending order of proportion_difference.
        //  This should get you an array where... slots for DCs who are most behind target come first,
        //  but their times are randomised, so you can just select the 1st one.
        slotsInHour.sort(() => (Math.random() * 2) - 1);
        slotsInHour.sort((slotA, slotB) => slotA.proportion_difference - slotB.proportion_difference);
        return slotsInHour[0];
    }

    topUpSlotsByDayAndShift(day, shiftName, currentSlots) {
        const shortfall = 4 - currentSlots.length;
        if (shortfall <= 0) return [];

        const dayTimestamp = day.date.getTime();
        let slotsInDayShift = this.deduplicatedSlots.filter(slot => {
            const slotDayDate = clone(slot.starts_at);
            slotDayDate.setHours(0, 0, 0, 0);

            if (slotDayDate.getTime() !== dayTimestamp) return false;//  Filter out if slot is on wrong day
            if (slot.shift !== shiftName) return false;//  Filter out if slot is in wrong shift

            if (currentSlots.find(cs => cs.id === slot.id)) return false;//  Filter out any slots already selected
        });

        const topUpSelection = [];
        let preferredDCSlotsInShift = slotsInDayShift.filter(slot => slot.dc_id === this.preferredDCId);
        let topUpSlot;

        for (let i = 0; i < shortfall; i++) {
            if (this.preferredDCId) {
                if (preferredDCSlotsInShift.length) {
                    topUpSlot = clone(this.randomSlotFrom(preferredDCSlotsInShift));
                    topUpSelection.push(topUpSlot);
                    preferredDCSlotsInShift = preferredDCSlotsInShift.filter(s => s.id !== topUpSlot.id);
                    slotsInDayShift = slotsInDayShift.filter(s => s.id !== topUpSlot.id);
                }
            }
            else {
                if (slotsInDayShift.length) {
                    slotsInDayShift.sort(() => (Math.random() * 2) - 1);
                    slotsInDayShift.sort((slotA, slotB) => slotA.proportion_difference - slotB.proportion_difference);
                    topUpSlot = clone(slotsInDayShift[0]);
                    topUpSelection.push(topUpSlot);
                    preferredDCSlotsInShift = preferredDCSlotsInShift.filter(s => s.id !== topUpSlot.id);
                    slotsInDayShift = slotsInDayShift.filter(s => s.id !== topUpSlot.id);
                }
            }
        }

        return topUpSelection;
    }

    randomSlotFrom(slots) {
        if (!slots) return;
        if (!slots.length) return;
        if (slots.length > 1) slots.sort(() => (Math.random() * 2) - 1);
        return slots[0];
    }

    compileSlotsByDay() {
        this.selection.forEach(slot => {
            const dateText = dateFormat(slot.starts_at, "dddd D MMM YYYY");
            let dayOfSlots = this.slotsByDay.find(day => day.dateText == dateText);
            if (!dayOfSlots) {
                const date = clone(slot.starts_at);
                date.setHours(0, 0, 0, 0);//    slot.starts_at could be any time of day
                dayOfSlots = { date, dateText, slots: [] };
                this.slotsByDay.push(dayOfSlots);
            }
            dayOfSlots.slots.push(slot); //    It's an array, it's by reference, you can add to it after it's been pushed to categorisedSlots.
        });

        if(this.showDevLog) {
            console.log(`compileSlotsByDay finished`, this.selection);
        }
    }
}

export default new AvailableSlotsCompiler();
